// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {
	BLIK_PAYMENT_METHOD,
	PAYMENT_PROVIDER_NAME,
	PIX_PAYMENT_METHOD,
	PREMIUM_PRODUCT_FULL_NAME,
	PRODUCT_NAME,
	UPI_PAYMENT_METHOD,
} from '@app/features/app/config/I18nDisplayConstants';
import {ConduitFailure} from '@app/features/platform/types/EndpointError';
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {CheckoutPaymentMethod, PriceIds} from '@app/features/premium/commands/PremiumCommands';
import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import type {PricingMode} from '@app/features/premium/utils/PricingUtils';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {APIErrorCodes} from '@app/vendor/constants/ApiErrorCodes';
import {useLingui} from '@lingui/react/macro';
import {useCallback, useState} from 'react';

const logger = new Logger('useCheckoutActions');

type Plan = 'monthly' | 'yearly' | 'gift_1_month' | 'gift_1_year';

type CheckoutPromptKind = 'payment' | 'localized_card_preapproval';
function requiresLocalizedCardPreapproval(
	_plan: Plan,
	_currency: string | null | undefined,
	_isGift: boolean,
): boolean {
	return false;
}
function alternativePaymentMethodForCurrency(
	currency: string | null | undefined,
	isGift: boolean,
	plan: Plan,
): CheckoutPaymentMethod | null {
	if (isGift || (plan !== 'monthly' && plan !== 'yearly')) {
		return null;
	}
	if (currency === 'BRL') return 'pix';
	if (currency === 'INR') return 'upi';
	return null;
}
export const useCheckoutActions = (
	priceIds: PriceIds | null,
	countryCode: string | null,
	pricingMode: PricingMode,
	isGiftSubscription: boolean,
	mobileEnabled: boolean,
) => {
	const {t} = useLingui();
	const [loadingCheckout, setLoadingCheckout] = useState(false);
	const getAlternativePaymentMethodPrompt = useCallback(
		(
			currency: string | null | undefined,
			method: CheckoutPaymentMethod,
		): {description: string; primaryText: string} => {
			if (method === 'pix' && currency === 'BRL') {
				return {
					description: t`Pay with ${PIX_PAYMENT_METHOD} Automático to authorize recurring charges directly from your Brazilian bank. Or choose Use Card to enter a credit card on ${PAYMENT_PROVIDER_NAME}'s next screen.`,
					primaryText: t`Use ${PIX_PAYMENT_METHOD}`,
				};
			}
			if (method === 'upi' && currency === 'INR') {
				return {
					description: t`Pay with ${UPI_PAYMENT_METHOD} to set up an RBI-compliant e-mandate from your Indian bank. Or choose Use Card to enter a credit card on ${PAYMENT_PROVIDER_NAME}'s next screen.`,
					primaryText: t`Use ${UPI_PAYMENT_METHOD}`,
				};
			}
			return {
				description: t`Choose how you'd like to pay for this subscription.`,
				primaryText: t`Use Alternative Method`,
			};
		},
		[t],
	);
	const getLocalizedCardPrompt = useCallback(
		(currency: string | null | undefined): {description: string; secondaryText: string} => {
			switch (currency) {
				case 'BRL':
					return {
						description: t`Use Local Card to verify a card issued in Brazil before the paid BRL checkout. Choose Other Methods if you'd rather try ${PIX_PAYMENT_METHOD} or another payment method ${PAYMENT_PROVIDER_NAME} offers on the next screen.`,
						secondaryText: t`Other Methods`,
					};
				case 'INR':
					return {
						description: t`Use Local Card to verify a card issued in India before the paid INR checkout. Choose Other Methods if you'd rather try ${UPI_PAYMENT_METHOD} or another payment method ${PAYMENT_PROVIDER_NAME} offers on the next screen.`,
						secondaryText: t`Other Methods`,
					};
				case 'PLN':
					return {
						description: t`Use Local Card to verify a card issued in Poland before the paid PLN checkout. ${BLIK_PAYMENT_METHOD} does not support subscriptions in ${PAYMENT_PROVIDER_NAME} Checkout, so continue only if you want ${PAYMENT_PROVIDER_NAME}'s standard payment screen instead.`,
						secondaryText: t`Continue to Checkout`,
					};
				case 'TRY':
					return {
						description: t`Use Local Card to verify a card issued in Türkiye before the paid TRY checkout. There isn't an app-based local subscription method here, so continuing will take you to ${PAYMENT_PROVIDER_NAME}'s standard payment screen.`,
						secondaryText: t`Continue to Checkout`,
					};
				default:
					return {
						description: t`Use Local Card to verify a card issued in your billing country before the paid checkout. Choose Other Methods for any other payment method ${PAYMENT_PROVIDER_NAME} offers on the next screen.`,
						secondaryText: t`Other Methods`,
					};
			}
		},
		[t],
	);
	const handleCheckoutError = useCallback(
		(error: unknown) => {
			logger.error('Failed to create checkout session', error);
			if (error instanceof ConduitFailure) {
				const body = error.body;
				if (body && typeof body === 'object' && 'code' in body && typeof body.code === 'string') {
					if (body.code === APIErrorCodes.EMAIL_VERIFICATION_REQUIRED) {
						ToastCommands.error(t`Verify your email to purchase ${PREMIUM_PRODUCT_FULL_NAME}.`);
						return;
					}
					if (body.code === APIErrorCodes.PREMIUM_PURCHASE_BLOCKED) {
						ToastCommands.error(
							t`You're already Lifetime. Starting a new subscription isn't allowed. You can still buy gifts for others.`,
						);
						return;
					}
				}
			}
			ToastCommands.error(t`Failed to start checkout. Please try again.`);
		},
		[t],
	);
	const handleSelectPlan = useCallback(
		async (plan: Plan) => {
			if (loadingCheckout) return;
			logger.info('Plan selected', {plan, isGiftSubscription});
			if (isGiftSubscription && (plan === 'monthly' || plan === 'yearly')) {
				ToastCommands.error(
					t`You're currently on a gift subscription. It won't renew. You can redeem more gift codes to extend it. Recurring subscriptions can be started after your gift time ends.`,
				);
				return;
			}
			if (!priceIds) {
				logger.error('Price IDs not loaded yet');
				ToastCommands.error(t`Please wait for pricing information to load.`);
				return;
			}
			const planConfig: Record<Plan, {id: string | null; gift?: boolean}> = {
				monthly: {id: priceIds.monthly ?? null},
				yearly: {id: priceIds.yearly ?? null},
				gift_1_month: {id: priceIds.gift_1_month ?? null, gift: true},
				gift_1_year: {id: priceIds.gift_1_year ?? null, gift: true},
			};
			const selected = planConfig[plan];
			const priceId = selected.id;
			const isGift = selected.gift ?? false;
			if (!priceId) {
				logger.error('Price ID not available for plan', {plan});
				ToastCommands.error(t`This plan is not available at the moment. Please contact support.`);
				return;
			}
			const openCheckoutUrl = async (
				checkoutUrl: string,
				{
					promptKind = 'payment',
					skipMobilePrompt = false,
				}: {promptKind?: CheckoutPromptKind; skipMobilePrompt?: boolean} = {},
			) => {
				if (mobileEnabled && !skipMobilePrompt) {
					ModalCommands.push(
						modal(() => (
							<ConfirmModal
								title={promptKind === 'localized_card_preapproval' ? t`Verify Card` : t`Complete Payment`}
								description={
									promptKind === 'localized_card_preapproval'
										? t`${PAYMENT_PROVIDER_NAME} will first verify that your card is eligible for localized pricing, then continue you to payment. Return to ${PRODUCT_NAME} once you've completed it.`
										: t`You are now navigating to ${PAYMENT_PROVIDER_NAME} to complete the payment. Return to ${PRODUCT_NAME} once you've completed it!`
								}
								primaryText={t`OK`}
								primaryVariant="primary"
								secondaryText={t`Cancel`}
								onPrimary={() => {
									void openExternalUrl(checkoutUrl);
								}}
							/>
						)),
					);
					return;
				}
				await openExternalUrl(checkoutUrl);
			};
			const startCheckout = async ({
				skipMobilePrompt = false,
				paymentMethod,
			}: {
				skipMobilePrompt?: boolean;
				paymentMethod?: CheckoutPaymentMethod;
			} = {}) => {
				setLoadingCheckout(true);
				try {
					const checkoutUrl = await PremiumCommands.createCheckoutSession(
						priceId,
						countryCode ?? undefined,
						isGift,
						pricingMode,
						paymentMethod,
					);
					await openCheckoutUrl(checkoutUrl, {promptKind: 'payment', skipMobilePrompt});
				} catch (error) {
					handleCheckoutError(error);
				} finally {
					setLoadingCheckout(false);
				}
			};
			const startLocalizedCardPreapproval = async ({skipMobilePrompt = false}: {skipMobilePrompt?: boolean} = {}) => {
				if (!countryCode) {
					ToastCommands.error(t`Localized card verification is not available right now. Please try again later.`);
					return;
				}
				setLoadingCheckout(true);
				try {
					const checkoutUrl = await PremiumCommands.createLocalizedCardPreapprovalSession(
						priceId,
						countryCode,
						pricingMode,
					);
					await openCheckoutUrl(checkoutUrl, {
						promptKind: 'localized_card_preapproval',
						skipMobilePrompt,
					});
				} catch (error) {
					handleCheckoutError(error);
				} finally {
					setLoadingCheckout(false);
				}
			};
			if (requiresLocalizedCardPreapproval(plan, priceIds.currency, isGift)) {
				const localizedCardPrompt = getLocalizedCardPrompt(priceIds.currency);
				ModalCommands.push(
					modal(() => (
						<ConfirmModal
							title={t`Choose Payment Method`}
							description={localizedCardPrompt.description}
							primaryText={t`Use Local Card`}
							primaryVariant="primary"
							secondaryText={localizedCardPrompt.secondaryText}
							onPrimary={() => startLocalizedCardPreapproval({skipMobilePrompt: true})}
							onSecondary={() => {
								void startCheckout({skipMobilePrompt: true});
							}}
						/>
					)),
				);
				return;
			}
			const altPaymentMethod = alternativePaymentMethodForCurrency(priceIds.currency, isGift, plan);
			if (altPaymentMethod) {
				const altPrompt = getAlternativePaymentMethodPrompt(priceIds.currency, altPaymentMethod);
				ModalCommands.push(
					modal(() => (
						<ConfirmModal
							title={t`Choose Payment Method`}
							description={altPrompt.description}
							primaryText={altPrompt.primaryText}
							primaryVariant="primary"
							secondaryText={t`Use Card`}
							onPrimary={() => {
								void startCheckout({skipMobilePrompt: true, paymentMethod: altPaymentMethod});
							}}
							onSecondary={() => {
								void startCheckout({skipMobilePrompt: true});
							}}
						/>
					)),
				);
				return;
			}
			await startCheckout();
		},
		[
			handleCheckoutError,
			loadingCheckout,
			priceIds,
			countryCode,
			getLocalizedCardPrompt,
			getAlternativePaymentMethodPrompt,
			isGiftSubscription,
			mobileEnabled,
			pricingMode,
			t,
		],
	);
	return {
		loadingCheckout,
		handleSelectPlan,
	};
};
